import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/auth';
import { useLanguageUtils } from '@/context/language-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Menu, MessageSquare, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LanguageSelector from '@/components/language/LanguageSelector';
import { supabase } from '@/integrations/supabase/client';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet"

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation(['navbar', 'common']);
  const { getUrlWithLanguage, currentLanguage } = useLanguageUtils();
  const navigate = useNavigate();
  const location = useLocation();
  const [userAvatar, setUserAvatar] = useState<string | null>(null);

  // Fetch user avatar URL
  useEffect(() => {
    const fetchAvatar = async () => {
      if (user?.id) {
        const { data } = supabase.storage
          .from('avatars') // Assuming 'avatars' bucket
          .getPublicUrl(`public/${user.id}.png`); // Example path structure
          
        // Check if the URL is valid (basic check)
        if (data?.publicUrl && data.publicUrl.includes(user.id)) {
             // More robust check: try to fetch the image header
             try {
                 const response = await fetch(data.publicUrl, { method: 'HEAD' });
                 if (response.ok) {
                     setUserAvatar(data.publicUrl);
                 } else {
                      setUserAvatar(null); // Reset if HEAD request fails
                 }
             } catch (error) {
                  console.warn('Error checking avatar existence:', error);
                  setUserAvatar(null);
             }
        } else {
          setUserAvatar(null);
        }
      }
    };

    fetchAvatar();
  }, [user?.id]); // Re-fetch if user ID changes

  const handleLogout = () => {
    logout();
    navigate(getUrlWithLanguage('/login'));
  };

  const toggleTheme = () => {
    // Simple theme toggle using HTML data attribute
    const html = document.documentElement;
    const currentTheme = html.getAttribute('data-theme') || 'light';
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    html.setAttribute('data-theme', newTheme);
  };

  // Don't render header on login/register pages based on path
  const authPaths = [`/${currentLanguage}/login`, `/${currentLanguage}/register`];
  if (authPaths.includes(location.pathname)) {
     return null;
  }

  // Reusable NavLink component for Sheet
  const NavLink = ({ to, children }: { to: string, children: React.ReactNode }) => (
    <SheetClose asChild>
      <Link 
        to={getUrlWithLanguage(to)}
        className="block py-2 px-4 text-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
      >
        {children}
      </Link>
    </SheetClose>
  );

  return (
    <header className="bg-background border-b sticky top-0 z-50">
      <div className="container flex items-center justify-between h-16">
        {/* Logo - Always visible */}
        <Link to={getUrlWithLanguage('/')} className="font-bold text-lg text-primary">
          {t('navbar.brand')}
        </Link>

        {/* Desktop Navigation (Hidden on small screens) */}
        <nav className="hidden md:flex items-center space-x-4">
          <Link to={getUrlWithLanguage('/explore')} className="text-foreground hover:text-primary transition-colors">
            {t('navbar.explore')}
          </Link>
          <Link to={getUrlWithLanguage('/sugar-teaser')} className="text-foreground hover:text-primary transition-colors">
            {t('navbar.sugar_dating')}
          </Link>
          <Link to={getUrlWithLanguage('/pricing')} className="text-foreground hover:text-primary transition-colors">
            {t('navbar.pricing')}
          </Link>
          <Link to={getUrlWithLanguage('/how-it-works')} className="text-foreground hover:text-primary transition-colors">
            {t('navbar.how_it_works')}
          </Link>
        </nav>

        {/* Right side icons and menu */}
        <div className="flex items-center space-x-2 md:space-x-4">
          {/* Language Switcher */}
          <LanguageSelector />

          {/* Messages Icon */}
          {user && (
            <Link to={getUrlWithLanguage('/messages')} title={t('navbar:navigation.messages', 'Messages')}>
              <Button variant="ghost" size="icon" className="text-gray-600 hover:text-pink-600 dark:text-gray-300 dark:hover:text-pink-400">
                <MessageSquare className="h-5 w-5" />
              </Button>
            </Link>
          )}

          {/* User Menu / Auth Actions (Desktop) */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative w-8 h-8 rounded-full">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={userAvatar || undefined} alt={user.email || 'User'} />
                      <AvatarFallback>{user.email ? user.email.charAt(0).toUpperCase() : 'U'}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuItem className="font-normal text-sm px-4 py-2 hover:bg-accent hover:text-accent-foreground" asChild>
                    <Link to={getUrlWithLanguage('/dashboard')}>{t('navbar.dashboard')}</Link>
                  </DropdownMenuItem>
                  {user?.role === 'admin' && (
                    <DropdownMenuItem className="font-normal text-sm px-4 py-2 hover:bg-accent hover:text-accent-foreground" asChild>
                      <Link to={getUrlWithLanguage('/admin')}>{t('navbar.admin')}</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="font-normal text-sm px-4 py-2 hover:bg-accent hover:text-accent-foreground">
                    <button onClick={handleLogout} className="w-full text-left">{t('navbar.logout')}</button>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to={getUrlWithLanguage('/login')} className="text-foreground hover:text-primary transition-colors">
                  {t('navbar.login')}
                </Link>
                <Button asChild>
                  <Link to={getUrlWithLanguage('/register')}>{t('navbar.register')}</Link>
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Trigger (Visible on small screens) */}
          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">{t('navbar.open_menu', 'Open menu')}</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px]">
                <SheetHeader className="flex flex-row justify-between items-center space-x-2 mb-4">
                  <SheetTitle className="text-lg font-semibold">{t('navbar.menu', 'Menu')}</SheetTitle>
                  <SheetClose asChild>
                    <button className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
                      <X className="h-4 w-4" />
                      <span className="sr-only">{t('common:close')}</span>
                    </button>
                  </SheetClose>
                </SheetHeader>
                
                {/* Mobile Navigation Links */}
                <nav className="flex flex-col space-y-2 mb-6">
                  <NavLink to="/explore">{t('navbar.explore')}</NavLink>
                  <NavLink to="/sugar-teaser">{t('navbar.sugar_dating')}</NavLink>
                  <NavLink to="/pricing">{t('navbar.pricing')}</NavLink>
                  <NavLink to="/how-it-works">{t('navbar.how_it_works')}</NavLink>
                </nav>

                {/* Mobile User Actions / Auth */}
                <div className="border-t pt-4">
                  {user ? (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-3 px-4 py-2">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={userAvatar || undefined} alt={user.email || 'User'} />
                          <AvatarFallback>{user.email ? user.email.charAt(0).toUpperCase() : 'U'}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium truncate">{user.email}</span>
                      </div>
                      <NavLink to="/dashboard">{t('navbar.dashboard')}</NavLink>
                      {user?.role === 'admin' && (
                        <NavLink to="/admin">{t('navbar.admin')}</NavLink>
                      )}
                      <SheetClose asChild>
                        <button 
                          onClick={handleLogout} 
                          className="block w-full text-left py-2 px-4 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-md transition-colors"
                        >
                          {t('navbar.logout')}
                        </button>
                      </SheetClose>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <NavLink to="/login">{t('navbar.login')}</NavLink>
                      <SheetClose asChild>
                        <Button asChild className="w-full justify-start py-2 px-4">
                          <Link to={getUrlWithLanguage('/register')}>{t('navbar.register')}</Link>
                        </Button>
                      </SheetClose>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
